
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from as observableFrom, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfirmationModalComponent } from 'src/app/common-components/modals/confirmation-modal/confirmation-modal.component';
import { GenericModal } from 'src/app/interface/data.interface';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';

/**
 * Registered once via HttpInterceptorProviders (AppModule).
 * Do not re-provide in SharedModule / feature modules (VDS-3328).
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor, OnDestroy {
  private dataService: DataService | null = null;
  private authService: AuthService | null = null;
  private fbToken = '';
  private readonly INVALID_CREDENTIALS_EXCEPTION = 'invalid-credentials-exception';
  private readonly RETRY_ATTEMPTED_FLAG = 'X-Retry-Attempted';
  private readonly GRANT_EXCHANGE_ATTEMPTED_FLAG = 'X-Grant-Exchange-Attempted';
  private dialogRefFirebaseMismatch: MatDialogRef<ConfirmationModalComponent, boolean> | null =
    null;
  /** Loose type: @angular/fire nests its own rxjs Subscription copy. */
  private authStateSubscription: { unsubscribe: () => void } | null = null;

  constructor(
    public afAuth: AngularFireAuth,
    private injector: Injector,
  ) {
    this.initializeFbToken();
  }

  ngOnDestroy(): void {
    this.authStateSubscription?.unsubscribe();
    this.authStateSubscription = null;
  }

  private ensureServices(): void {
    if (!this.dataService) {
      this.dataService = this.injector.get(DataService);
    }
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
  }

  private initializeFbToken(): void {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (user && user.fbToken) {
        this.fbToken = user.fbToken;
      }
    } catch {
      /* ignore */
    }

    this.authStateSubscription?.unsubscribe();
    this.authStateSubscription = this.afAuth.authState.subscribe((fbUser) => {
      if (fbUser) {
        fbUser
          .getIdToken(false)
          .then((token) => {
            let currentUser: { email?: string; fbToken?: string } | null = null;
            try {
              const raw = localStorage.getItem('currentUser');
              currentUser = raw ? JSON.parse(raw) : null;
            } catch {
              currentUser = null;
            }

            const fbEmail = (fbUser.email ?? '').toLowerCase().trim();
            const curEmail = (currentUser?.email ?? '').toLowerCase().trim();
            const sameFirebaseAccount = fbEmail && curEmail && fbEmail === curEmail;

            // Multisesión: no sobrescribir fb-token de otra cuenta.
            if (sameFirebaseAccount && currentUser) {
              this.fbToken = token;
              if (currentUser.fbToken !== token) {
                currentUser.fbToken = token;
                localStorage.setItem('currentUser', JSON.stringify(currentUser));
              }
            } else if (currentUser?.fbToken) {
              this.fbToken = currentUser.fbToken;
            }
          })
          .catch((error) => {
            console.error('Error getting Firebase token:', error);
            if (
              error.code === 'auth/user-token-expired' ||
              error.code === 'auth/network-request-failed'
            ) {
              console.warn('Firebase token expired or network error');
            }
          });
      } else {
        this.fbToken = '';
      }
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.headers.has(this.RETRY_ATTEMPTED_FLAG)) {
      return next.handle(req);
    }

    this.ensureServices();
    return observableFrom(this.handleAccess(req, next));
  }

  private async handleAccess(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Promise<HttpEvent<unknown>> {
    if (request.headers.has(this.RETRY_ATTEMPTED_FLAG)) {
      return next.handle(request).toPromise();
    }

    // Grant exchange/revoke are grant-authenticated; do not attach stale Fb-Token / refresh loops.
    // Mint (POST .../session-grants) may carry an explicit Fb-Token for the account being
    // activated before localStorage is updated — preserve that header when present.
    const url = request.url || '';
    if (
      url.includes('/session-grants/exchange') ||
      (request.method === 'DELETE' && url.includes('/session-grants'))
    ) {
      return next.handle(request).toPromise();
    }

    this.ensureServices();
    const dataService = this.dataService as DataService;
    const authService = this.authService as AuthService;

    const lang = dataService.getAppLanguage();
    // VDS-3328 / global auth: localStorage.currentUser is the only credential source.
    // DataService.getUser() is stricter (fbToken + isAuth + buildings) and must NEVER gate
    // whether Authorization / Fb-Token are attached — that caused bare 401s after the
    // single-interceptor consolidation.
    const storedUser = this.readSessionUserFromStorage();
    // getUser() only as app-data fallback (roles, buildings shape) when storage is thin.
    const user = dataService.getUser() ?? storedUser;

    const isSessionGrantMint =
      request.method === 'POST' &&
      url.includes('/session-grants') &&
      !url.includes('/exchange');
    const explicitFbToken = isSessionGrantMint
      ? this.normalizeToken(
          request.headers.get('Fb-Token') || request.headers.get('fb-token'),
        )
      : '';

    const token = this.normalizeToken(storedUser?.token) || this.normalizeToken(user?.token);
    const fbTokenToUse =
      explicitFbToken ||
      this.normalizeToken(storedUser?.fbToken) ||
      this.normalizeToken(user?.fbToken) ||
      this.normalizeToken(this.fbToken);

    if (token || fbTokenToUse) {
      const sessionUser = storedUser || user;

      if (fbTokenToUse) {
        this.fbToken = fbTokenToUse;
      }

      let building = null;
      let language = null;

      if (
        sessionUser &&
        !sessionUser.superUser &&
        sessionUser.buildings &&
        sessionUser.buildings.length > 0
      ) {
        building = sessionUser.buildings[0]._id ?? sessionUser.buildings[0].id;
      }

      if (lang) {
        language = lang;
      }

      try {
        if (localStorage.getItem('currentUser')) {
          authService.openModalDatabaseUseAuthorization();
        }
      } catch (error) {
        console.error('openModalDatabaseUseAuthorization failed:', error);
      }

      request = this.applyCredentials(request, fbTokenToUse, token, building, language);
    }

    // Auth failures (VDS-3327): HTTP 401 and/or invalid-credentials code → refresh.
    // 403 (permission) and business 406 must NOT trigger logout/refresh here.
    // Bare 401 "No auth token" (no Authorization header) cannot be fixed by Firebase refresh
    // (VDS-3328 follow-up) — only refresh when Bearer was sent or invalid-credentials code.
    return next
      .handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const alreadyAttempted = (error as { __tokenRefreshAttempted?: boolean })
            .__tokenRefreshAttempted === true;

          const errorCode = error.error?.error?.code || error.error?.code;
          const hasRetryFlag = request.headers.has(this.RETRY_ATTEMPTED_FLAG);
          const isInvalidCredentialsCode =
            typeof errorCode === 'string' &&
            errorCode.includes(this.INVALID_CREDENTIALS_EXCEPTION);
          const authorization = request.headers.get('Authorization') || '';
          const hadAuthorization =
            authorization.length > 0 && !/^Bearer\s*$/i.test(authorization);
          const isUnauthorizedStatus = error.status === 401 && hadAuthorization;
          const isAuthFailure = isUnauthorizedStatus || isInvalidCredentialsCode;

          if (isAuthFailure && (user || storedUser) && !hasRetryFlag && !alreadyAttempted) {
            return this.handleInvalidCredentialsError(
              request,
              next,
              user || storedUser,
            );
          }
          return throwError(() => error);
        }),
      )
      .toPromise();
  }

  /** Raw currentUser from storage — used when DataService.getUser() is too strict. */
  private readSessionUserFromStorage(): {
    token?: string;
    fbToken?: string;
    email?: string;
    superUser?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildings?: any[];
    sessionGrant?: string;
  } | null {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private handleInvalidCredentialsError(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    user: any,
  ): Observable<HttpEvent<unknown>> {
    this.ensureServices();
    const dataService = this.dataService as DataService;
    const authService = this.authService as AuthService;

    return observableFrom(this.afAuth.currentUser).pipe(
      switchMap((fbUser) => {
        const curEmail = (user?.email ?? '').toLowerCase().trim();
        const fbEmail = (fbUser?.email ?? '').toLowerCase().trim();
        if (!fbUser || !curEmail || fbEmail !== curEmail) {
          // VDS-3308: try silent re-auth via session grant before mismatch modal
          if (user?.sessionGrant && !request.headers.has(this.GRANT_EXCHANGE_ATTEMPTED_FLAG)) {
            return observableFrom(authService.activateAccountWithGrant(user)).pipe(
              switchMap((ok: boolean) => {
                if (!ok) {
                  return throwError(() => new Error('firebase-session-mismatch'));
                }
                const refreshed = dataService.getUser() || user;
                const lang = dataService.getAppLanguage();
                let building = null;
                let language = null;
                if (
                  refreshed &&
                  !refreshed.superUser &&
                  refreshed.buildings &&
                  refreshed.buildings.length > 0
                ) {
                  building = refreshed.buildings[0]._id;
                }
                if (lang) {
                  language = lang;
                }
                this.fbToken = refreshed.fbToken || this.fbToken;
                const newRequest = this.applyCredentials(
                  request,
                  refreshed.fbToken,
                  refreshed?.token || user.token,
                  building,
                  language,
                ).clone({
                  setHeaders: {
                    [this.RETRY_ATTEMPTED_FLAG]: 'true',
                    [this.GRANT_EXCHANGE_ATTEMPTED_FLAG]: 'true',
                  },
                });
                return next.handle(newRequest);
              }),
            );
          }
          return throwError(() => new Error('firebase-session-mismatch'));
        }
        return observableFrom(authService.hasActiveFirebaseSession()).pipe(
          switchMap((hasSession: boolean) => {
            if (hasSession) {
              return observableFrom(authService.refreshFirebaseToken()).pipe(
                switchMap((newToken: string | null) => {
                  if (newToken) {
                    dataService.clearUserCache();
                    const updatedUser = dataService.getUser();
                    const userToUse = updatedUser || user;

                    if (userToUse) {
                      userToUse.fbToken = newToken;
                      dataService.setUser(userToUse);
                      this.updateUserInMultisession(userToUse);
                    }

                    const lang = dataService.getAppLanguage();
                    let building = null;
                    let language = null;

                    if (
                      userToUse &&
                      !userToUse.superUser &&
                      userToUse.buildings &&
                      userToUse.buildings.length > 0
                    ) {
                      building = userToUse.buildings[0]._id;
                    }

                    if (lang) {
                      language = lang;
                    }

                    this.fbToken = newToken;

                    const newRequest = this.applyCredentials(
                      request,
                      newToken,
                      userToUse?.token || user.token,
                      building,
                      language,
                    ).clone({
                      setHeaders: {
                        [this.RETRY_ATTEMPTED_FLAG]: 'true',
                      },
                    });

                    return next.handle(newRequest).pipe(
                      catchError((retryError: HttpErrorResponse) => {
                        const markedError = new HttpErrorResponse({
                          error: retryError.error,
                          status: retryError.status,
                          statusText: retryError.statusText,
                          url: retryError.url,
                          headers: retryError.headers,
                        });
                        (markedError as { __tokenRefreshAttempted?: boolean }).__tokenRefreshAttempted =
                          true;
                        return throwError(() => markedError);
                      }),
                    );
                  }
                  return throwError(() => new Error('Token refresh failed'));
                }),
              );
            }
            return throwError(() => new Error('No active Firebase session'));
          }),
        );
      }),
      catchError((error) => {
        console.error('Error en handleInvalidCredentialsError:', error);
        if (error instanceof Error && error.message === 'firebase-session-mismatch') {
          this.openFirebaseSessionMismatchModal();
        }
        return throwError(() => error);
      }),
    );
  }

  private openFirebaseSessionMismatchModal(): void {
    if (this.dialogRefFirebaseMismatch) return;

    this.ensureServices();
    const matDialog = this.injector.get(MatDialog);
    const translate = this.injector.get(TranslateService);

    const modalData: GenericModal = {
      title: 'auth.sessionMismatch.title',
      subtitle: translate.instant('auth.sessionMismatch.subtitle'),
      note: 'auth.sessionMismatch.note',
      labelButtonCancel: 'buttons.close',
      labelButtonSave: 'auth.sessionMismatch.logoutCta',
      size: 'small',
    };

    this.dialogRefFirebaseMismatch = matDialog.open(ConfirmationModalComponent, {
      data: modalData,
      panelClass: ConfirmationModalComponent.getModalPanelClass(
        modalData.size,
        !!modalData.subtitle,
      ),
      autoFocus: false,
      restoreFocus: true,
    });

    this.dialogRefFirebaseMismatch.afterClosed().subscribe((result) => {
      this.dialogRefFirebaseMismatch = null;
      if (result) {
        // Scoped logout: only the active account (keeps other usersSessions — VDS-3330)
        (this.authService as AuthService).doLogout();
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateUserInMultisession(user: any): void {
    try {
      const dataService = this.dataService as DataService;
      const sessions = dataService.getUserInMultisession();
      if (sessions && sessions.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sessionIndex = sessions.findIndex((s: any) => s.email === user.email);
        if (sessionIndex !== -1) {
          sessions[sessionIndex].fbToken = user.fbToken;
          dataService.setUserInMultisesion(sessions[sessionIndex]);
        }
      }
    } catch (error) {
      console.error('Error actualizando usersSessions:', error);
    }
  }

  /** Drops empty / literal "null" / "undefined" values from legacy service headers. */
  private normalizeToken(value: unknown): string {
    if (value == null) {
      return '';
    }
    const raw = String(value).trim();
    if (!raw || raw === 'null' || raw === 'undefined') {
      return '';
    }
    return raw;
  }

  applyCredentials(
    req: HttpRequest<unknown>,
    fbToken: string | undefined,
    token: string | undefined,
    buildingId: string | null | undefined,
    language: string | null | undefined,
  ): HttpRequest<unknown> {
    // Always strip legacy Authorization / Fb-Token (stale constructor headers, empty
    // strings, lowercase aliases) and rebuild from the canonical session.
    let headers = req.headers
      .delete('Authorization')
      .delete('authorization')
      .delete('Fb-Token')
      .delete('fb-token');

    const items: Record<string, string> = {
      platform: 'webpage',
    };

    const normalizedToken = this.normalizeToken(token);
    if (normalizedToken) {
      items['Authorization'] = /^bearer\s+/i.test(normalizedToken)
        ? normalizedToken
        : `Bearer ${normalizedToken}`;
    }

    const normalizedFb = this.normalizeToken(fbToken);
    if (normalizedFb) {
      items['Fb-Token'] = normalizedFb;
    }

    if (buildingId) {
      items['buildingId'] = String(buildingId);
    }

    if (language) {
      items['munilyLanguage'] = language;
    }

    return req.clone({ headers, setHeaders: items });
  }
}
